import React, { useState } from 'react';
import TechlogEntryForm from './components/TechlogEntryForm';
import CrewStatusDashboard from './components/CrewStatusDashboard';
import AircraftStatusDashboard from './components/AircraftStatusDashboard';
import TechlogSummary from './components/TechlogSummary';
import CrewManagement from './components/CrewManagement';
import AircraftManagement from './components/AircraftManagement';
import AuthWrapper from './components/AuthWrapper';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TechlogEntry } from './types';

const AppContent: React.FC = () => {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<'entry' | 'summary' | 'crew' | 'aircraft' | 'crew-mgmt' | 'aircraft-mgmt'>('entry');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [editingEntry, setEditingEntry] = useState<TechlogEntry | null>(null);

  const handleEntryAdded = () => {
    setEditingEntry(null);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleEditEntry = (entry: TechlogEntry) => {
    setEditingEntry(entry);
    setActiveTab('entry');
  };

  const tabs = [
    { id: 'entry', label: 'Techlog Entry', icon: '📝' },
    { id: 'summary', label: 'Techlog Summary', icon: '📊' },
    { id: 'crew', label: 'Crew Status', icon: '👥' },
    { id: 'aircraft', label: 'Aircraft Status', icon: '✈️' },
    { id: 'crew-mgmt', label: 'Crew Management', icon: '👨‍✈️' },
    { id: 'aircraft-mgmt', label: 'Aircraft Management', icon: '🛩️' },
  ] as const;

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await signOut();
    }
  };

  // Show auth screen if not authenticated
  if (!user) {
    return <AuthWrapper />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <img 
                src="/sal_logo.jpg" 
                alt="Safari Air Link" 
                className="h-12 w-auto"
              />
              <div className="flex flex-col">
                <h1 className="text-xl font-bold text-gray-900">Safari Air Link</h1>
                <span className="text-xs text-gray-500">Crew & Aircraft Management System</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="text-sm text-gray-700">{user.email}</span>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'entry' && (
          <TechlogEntryForm 
            onEntryAdded={handleEntryAdded} 
            editingEntry={editingEntry}
          />
        )}
        
        {activeTab === 'summary' && (
          <TechlogSummary 
            refreshTrigger={refreshTrigger} 
            onEditEntry={handleEditEntry}
          />
        )}
        
        {activeTab === 'crew' && (
          <CrewStatusDashboard refreshTrigger={refreshTrigger} />
        )}
        
        {activeTab === 'aircraft' && (
          <AircraftStatusDashboard refreshTrigger={refreshTrigger} />
        )}
        
        {activeTab === 'crew-mgmt' && (
          <CrewManagement />
        )}
        
        {activeTab === 'aircraft-mgmt' && (
          <AircraftManagement />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-center text-sm text-gray-500">
            <p className="font-medium">Safari Air Link</p>
            <p className="mt-1">Crew & Aircraft Management System © {new Date().getFullYear()}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
